import { useMemo, useState } from 'react';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import cross from '~/assets/icons/cross.svg';
import gps from '~/assets/icons/gps.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { FadeContainer } from '~/components/ui/FadeContainer';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { getDistanceBetweenVectors3D, MapPointCategory } from '~/shared';
import { AppBackButton } from '../../components/AppBackButton';
import { SearchInput } from '../../components/SearchInput';
import {
	GPSCategoryIcons,
	GPSCategoryLabels,
	GPSCategoryTitles,
} from './PhoneGPS.model';
import {
	StyledActiveContainer,
	StyledActiveListItem,
	StyledCategoriesContainer,
	StyledCategoryContainer,
	StyledCategoryIcon,
	StyledContainer,
	StyledHead,
	StyledListContainer,
	StyledListItem,
	StyledSearchBackButton,
	StyledSearchContainer,
	StyledWrapper,
} from './PhoneGPS.styled';

const PhoneGPS = () => {
	const avaliableCategories = useObservable(rage.gps.getAvaliableCategories());
	const mapPoints = useObservable(rage.gps.getMapPoints());
	const [searchInputValue, setSearchInputValue] = useState('');
	const [listVisible, setListVisible] = useState(false);
	const [selectedCategory, setSelectedCategory] =
		useState<MapPointCategory | null>(null);
	const playerPosition = useObservable(rage.gps.getPlayerPosition());
	const activeRoute = useObservable(rage.gps.getActiveRoute());

	const searchActive = useMemo(
		() => searchInputValue.trim().length > 0,
		[searchInputValue],
	);

	const allMapPoints = useMemo(() => {
		return Object.values(mapPoints || {})
			.flat()
			.map((item) => ({
				...item,
				distance: playerPosition
					? Math.round(
							getDistanceBetweenVectors3D(playerPosition, item.position, true),
					  )
					: 0,
			}))
			.sort((a, b) => a.distance - b.distance);
	}, [mapPoints, playerPosition]);

	const activePoint = useMemo(() => {
		return allMapPoints?.find((item) => item.id === activeRoute?.id);
	}, [allMapPoints, activeRoute]);

	const filteredMapPoints = useMemo(() => {
		if (searchInputValue.trim().length > 0) {
			return allMapPoints?.filter((item) =>
				item.label.toLowerCase().includes(searchInputValue.toLowerCase()),
			);
		}

		if (selectedCategory) {
			return allMapPoints?.filter((item) => item.category === selectedCategory);
		}

		return allMapPoints || [];
	}, [searchInputValue, allMapPoints, selectedCategory]);

	const handleCloseSearch = () => {
		setSearchInputValue('');
	};

	return (
		<StyledWrapper>
			<StyledContainer
				style={{ height: searchActive || listVisible ? '506px' : '400px' }}
			>
				{listVisible && !searchActive ? (
					<StyledHead>
						<AppBackButton onClick={() => setListVisible(false)} />
						{selectedCategory && (
							<Text>{GPSCategoryTitles[selectedCategory]}</Text>
						)}
					</StyledHead>
				) : (
					<StyledSearchContainer>
						<StyledSearchBackButton
							$active={searchActive}
							onClick={handleCloseSearch}
						>
							<img src={arrowLeft} alt="arrowLeft" />
						</StyledSearchBackButton>
						<SearchInput
							value={searchInputValue}
							onChange={(value) => setSearchInputValue(value)}
							placeholder="Введите название / место для поиска"
							style={{
								width: searchActive ? 'calc(100% - 50px)' : '100%',
								transition: 'width 0.3s ease-in-out',
							}}
						/>
					</StyledSearchContainer>
				)}
				{listVisible || searchActive ? (
					<FadeContainer
						height={searchActive ? 366 : 396}
						fadeSize={50}
						style={{ marginTop: 20 }}
					>
						<StyledListContainer $search={searchActive} tabIndex={-1}>
							{filteredMapPoints.map((item, index) => (
								<StyledListItem
									key={index}
									$active={activeRoute?.id === item.id}
								>
									<img
										src={
											GPSCategoryIcons[
												item.category || MapPointCategory.IMPORTANT
											]
										}
									/>
									<Text>{item.label}</Text>
									<Text>
										{item.distance} <span>м</span>
									</Text>
									{activeRoute?.id === item.id ? (
										<Button
											color={ButtonColors.Cancel}
											height={ButtonHeight.Height30}
											onClick={() => {
												rage.gps
													.clearTargetRoute()
													.catch((err) =>
														rage.notifications.showException(err),
													);
											}}
										>
											<img src={cross} style={{ width: 14, height: 14 }} />
										</Button>
									) : (
										<Button
											color={ButtonColors.Select}
											height={ButtonHeight.Height30}
											onClick={() => {
												rage.gps
													.setTargetRoute(item)
													.catch((err) =>
														rage.notifications.showException(err),
													);
											}}
										>
											<img src={gps} style={{ width: 14, height: 14 }} />
										</Button>
									)}
								</StyledListItem>
							))}
						</StyledListContainer>
					</FadeContainer>
				) : (
					<FadeContainer
						height={activeRoute ? 216 : 266}
						fadeSize={50}
						style={{ marginTop: 20 }}
						tabIndex={-1}
					>
						<StyledCategoriesContainer $activePoint={!!activeRoute}>
							{avaliableCategories?.map((item, index) => (
								<StyledCategoryContainer
									key={index}
									onClick={() => {
										setSelectedCategory(item);
										setListVisible(true);
										setSearchInputValue('');
										playSound(Sound.ClickButton);
									}}
									onMouseEnter={() => playSound(Sound.LinkCursor)}
								>
									<StyledCategoryIcon>
										<img src={GPSCategoryIcons[item]} />
									</StyledCategoryIcon>
									<Text>{GPSCategoryLabels[item]}</Text>
								</StyledCategoryContainer>
							))}
						</StyledCategoriesContainer>
					</FadeContainer>
				)}
			</StyledContainer>
			{!!activeRoute && !listVisible && !searchActive && (
				<StyledActiveContainer>
					<StyledActiveListItem>
						<img
							src={
								GPSCategoryIcons[
									activePoint?.category || MapPointCategory.IMPORTANT
								]
							}
							style={{ width: 14, height: 14 }}
						/>
						<Text>{activePoint?.label}</Text>
						<Text>{activePoint?.distance} м</Text>
						<Button
							color={ButtonColors.Cancel}
							height={ButtonHeight.Height30}
							onClick={() => {
								rage.gps
									.clearTargetRoute()
									.catch((err) => rage.notifications.showException(err));
							}}
						>
							<img src={cross} style={{ width: 14, height: 14 }} />
						</Button>
					</StyledActiveListItem>
				</StyledActiveContainer>
			)}
		</StyledWrapper>
	);
};

export { PhoneGPS };
